/**
 * showErrorToast Utility
 * Helper function to display custom error toasts with copy/report functionality.
 * Use this instead of toast.error() for errors that should be copyable and reportable.
 */

import { toast } from "sonner";
import { ErrorToast } from "@/components/errors/ErrorToast";
import {
  type ReportableError,
  extractReportableError,
} from "@/types/error.types";
import { createElement } from "react";
import i18n from "@/i18n/index";
import { resolveMessage } from "@/utils/resolveErrorMessage";

interface ShowErrorToastOptions {
  /** Primary error message to display */
  message: string;
  /** Optional secondary description */
  description?: string;
  /** The error object (Error, API response, or unknown) */
  error?: unknown;
  /** Duration in ms before auto-dismiss (default: 15000, use Infinity for no auto-dismiss) */
  duration?: number;
  /** Context IDs to include with the error */
  workflowId?: string;
  executionId?: string;
  nodeId?: string;
  projectId?: string;
  timelineId?: string;
}

/**
 * Walk an unknown error envelope and return the inner `details.lock` object if
 * one is present. Mirrors the shape probing done by `extractStringField` in
 * `error.types.ts` (top-level, axios `response.data.error`, openapi-ts
 * `data.error`, and `body.error`) so this works regardless of which fetch
 * client surfaced the error.
 */
function extractLockInfo(input: unknown): Record<string, unknown> | undefined {
  if (typeof input !== "object" || input === null) return undefined;
  const obj = input as Record<string, unknown>;

  const fromErrorNode = (e: unknown): Record<string, unknown> | undefined => {
    if (typeof e !== "object" || e === null) return undefined;
    const details = (e as Record<string, unknown>)["details"];
    if (typeof details !== "object" || details === null) return undefined;
    const lock = (details as Record<string, unknown>)["lock"];
    return typeof lock === "object" && lock !== null
      ? (lock as Record<string, unknown>)
      : undefined;
  };

  const resp = obj["response"] as Record<string, unknown> | undefined;
  const respData = resp?.["data"] as Record<string, unknown> | undefined;
  const data = obj["data"] as Record<string, unknown> | undefined;
  const body = obj["body"] as Record<string, unknown> | undefined;

  return (
    fromErrorNode(obj["error"]) ??
    fromErrorNode(respData?.["error"]) ??
    fromErrorNode(data?.["error"]) ??
    fromErrorNode(body?.["error"])
  );
}

/**
 * When a CONFLICT is caused by the same user holding the workflow lock from
 * another tab, override the generic CONFLICT toast with a specific message.
 *
 * Cross-user conflicts at page-mount are routed through `WorkflowLockConflict`
 * (see `pages/Workflow.tsx`), so this override only needs to handle the
 * same-user case. Same-user lock transfer is intentionally forbidden — see
 * `_workflow.create_workflow_lock` and the design note in this file's PR.
 */
function lockConflictOverride(
  code: string | undefined,
  rawError: unknown,
): { message: string; description: string } | null {
  if (code !== "CONFLICT") return null;
  const lock = extractLockInfo(rawError);
  if (!lock || lock["iconName"] !== "workflow-locked-by-me") return null;
  return {
    message: i18n.t("errors.lock.workflowOpenInOtherTab", {
      defaultValue: "This workflow is already open in another tab.",
    }),
    description: i18n.t("errors.lock.workflowOpenInOtherTabDescription", {
      defaultValue:
        "Switch to that tab to continue editing — saves don't overwrite each other.",
    }),
  };
}

/**
 * Show an error toast with copy and report functionality.
 *
 * @example
 * ```ts
 * // Basic usage
 * showErrorToast({
 *   message: "Failed to save workflow",
 *   error: apiError,
 * });
 *
 * // With context
 * showErrorToast({
 *   message: "Node execution failed",
 *   description: "The AI service returned an error",
 *   error: executionError,
 *   workflowId: "abc-123",
 *   nodeId: "node-456",
 * });
 *
 * // Persistent (no auto-dismiss)
 * showErrorToast({
 *   message: "Critical error occurred",
 *   error: criticalError,
 *   duration: Infinity,
 * });
 * ```
 */
export function showErrorToast(
  options: ShowErrorToastOptions,
): string | number {
  const {
    message,
    description,
    error,
    duration = 15000,
    workflowId,
    executionId,
    nodeId,
    projectId,
    timelineId,
  } = options;

  // Extract reportable error from the error object
  const extracted: ReportableError = error
    ? extractReportableError(error, {
        workflowId,
        executionId,
        nodeId,
        projectId,
        timelineId,
      })
    : {
        message,
        description,
        timestamp: new Date().toISOString(),
        workflowId,
        executionId,
        nodeId,
        projectId,
        timelineId,
      };

  // Override with explicit options (immutable)
  const reportableError: ReportableError = {
    ...extracted,
    ...(message ? { message } : {}),
    ...(description ? { description } : {}),
  };

  // Translate via tk if available, interpolating any backend-supplied params
  let displayMessage = resolveMessage(
    reportableError.tk,
    reportableError.message,
    reportableError.params,
  );

  // Lock-conflict override — surfaces a specific message when the CONFLICT is
  // caused by the same user holding the workflow lock from another tab.
  // Falls through to the generic CONFLICT translation when not applicable.
  const lockOverride = lockConflictOverride(reportableError.code, error);
  if (lockOverride) {
    displayMessage = lockOverride.message;
    reportableError.description = lockOverride.description;
  }

  // Create and show the custom toast
  const toastId = toast.custom(
    (id) =>
      createElement(ErrorToast, {
        toastId: id,
        message: displayMessage,
        description: reportableError.description,
        error: reportableError,
      }),
    {
      duration,
      position: "bottom-right",
    },
  );

  return toastId;
}

/**
 * Helper to extract correlationId from various API error formats
 */
export function extractCorrelationId(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;

  // Direct correlationId property
  if ("correlationId" in error && typeof error.correlationId === "string") {
    return error.correlationId;
  }

  // Nested in data
  if ("data" in error && typeof error.data === "object" && error.data) {
    const data = error.data as Record<string, unknown>;
    if ("correlationId" in data && typeof data.correlationId === "string") {
      return data.correlationId;
    }
  }

  return undefined;
}
